"use client";

import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { PLANS } from "./ful-legend";
import { emitSignal } from "./signal";
import { isLapsedTrial, isRetrialEligible, TRIAL_DURATION_DAYS } from "./trial";
import { setDebugEnabled, getEffectiveDebugMode } from "./debug";

const AuthContext = createContext(null);
const _D = typeof window !== "undefined" && localStorage.getItem("fulkit-debug") === "1";

// Module-level flag: true once auth has resolved at least once this session.
// AuthGuard uses this to skip the splash on warm navigations.
let _authResolved = false;
export function hasAuthResolved() { return _authResolved; }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  const [githubConnected, setGithubConnected] = useState(false);
  const [compactMode, setCompactModeState] = useState(true);
  const [onboardingState, setOnboardingState] = useState(null);
  const [newlyConnectedIntegration, setNewlyConnectedIntegration] = useState(null);

  // Initialize compact mode from localStorage (default: compact/minimal)
  useEffect(() => {
    const stored = localStorage.getItem("fulkit-compact-mode");
    setCompactModeState(stored === null ? true : stored === "true");
  }, []);

  const setCompactMode = useCallback((val) => {
    setCompactModeState(val);
    localStorage.setItem("fulkit-compact-mode", String(val));
  }, []);

  const [hasContext, setHasContext] = useState(false);
  const fetchingRef = useRef(null); // dedup guard — prevents double-fetch on mount

  // Fetch profile from DB + check for context (onboarded OR has notes)
  const fetchProfile = useCallback(async (userId) => {
    // Dedup: if already fetching, await the existing promise instead of bailing
    if (fetchingRef.current) {
      _D && console.log("[auth:debug] fetchProfile DEDUP — awaiting existing fetch for", userId);
      return fetchingRef.current;
    }

    const _t0 = performance.now();
    _D && console.log("[auth:debug] fetchProfile START for", userId);

    const fetchPromise = (async () => {
    // 5s hard cap — never block auth resolution waiting for DB
    let _raceTimer = null;
    const results = await Promise.race([
      Promise.all([
        supabase.from("profiles").select("*").eq("id", userId).single().abortSignal(AbortSignal.timeout(5000)).then(r => { _D && console.log("[auth:debug] profiles query done", Math.round(performance.now() - _t0) + "ms", r.data ? "HAS DATA" : "NULL", r.error?.message || ""); return r; }),
        supabase.from("notes").select("*", { count: "exact", head: true }).eq("user_id", userId).abortSignal(AbortSignal.timeout(5000)).then(r => { _D && console.log("[auth:debug] notes count done", Math.round(performance.now() - _t0) + "ms", "count=" + r.count); return r; }),
        supabase.from("onboarding_progress").select("*").eq("user_id", userId).order("tier_num").abortSignal(AbortSignal.timeout(5000)).then(r => { _D && console.log("[auth:debug] onboarding_progress done", Math.round(performance.now() - _t0) + "ms"); return r; }).catch(() => ({ data: null })),
        supabase.from("onboarding_tiers").select("tier_num,completion_trigger").order("tier_num").abortSignal(AbortSignal.timeout(5000)).then(r => { _D && console.log("[auth:debug] onboarding_tiers done", Math.round(performance.now() - _t0) + "ms"); return r; }).catch(() => ({ data: null })),
      ]).then(r => { clearTimeout(_raceTimer); return r; }),
      new Promise(resolve => { _raceTimer = setTimeout(() => { _D && console.log("[auth:debug] fetchProfile 5s RACE TIMEOUT fired", Math.round(performance.now() - _t0) + "ms"); resolve([{ data: null, _timedOut: true }, { count: 0 }, { data: null }, { data: null }]); }, 5000); }),
    ]);
    let { data } = results[0];
    const { count } = results[1];
    const progressRows = results[2]?.data;
    const tierRows = results[3]?.data;
    _D && console.log("[auth:debug] fetchProfile race resolved", Math.round(performance.now() - _t0) + "ms", "timedOut=" + !!results[0]._timedOut, "hasData=" + !!data);

    // Timeout recovery: DB was slow on cold start — retry the read, don't upsert
    if (results[0]._timedOut) {
      _D && console.log("[auth:debug] timeout recovery: waiting 2s then retry");
      await new Promise(r => setTimeout(r, 2000));
      try {
        const { data: retryData } = await supabase
          .from("profiles").select("*").eq("id", userId).single()
          .abortSignal(AbortSignal.timeout(8000));
        _D && console.log("[auth:debug] retry result", Math.round(performance.now() - _t0) + "ms", retryData ? "GOT DATA" : "STILL NULL");
        if (retryData) data = retryData;
      } catch (retryErr) {
        _D && console.log("[auth:debug] retry FAILED", Math.round(performance.now() - _t0) + "ms", retryErr?.message);
      }
    }

    // Safety net: create profile if it doesn't exist (new user, trigger may not have fired).
    // ignoreDuplicates is critical — without it, a slow profile fetch that falls into
    // this branch would OVERWRITE an existing user's onboarded/seat_type/messages with
    // defaults, resetting their state. With ignoreDuplicates, the upsert is a no-op on
    // conflict and we refetch the real row.
    if (!data) {
      _D && console.log("[auth:debug] NO profile data — entering upsert path", Math.round(performance.now() - _t0) + "ms");
      const trialEndsAt = new Date(Date.now() + TRIAL_DURATION_DAYS * 86400000).toISOString();
      const { data: created } = await supabase
        .from("profiles")
        .upsert({ id: userId, seat_type: "trial", onboarded: false, messages_this_month: 0, trial_ends_at: trialEndsAt, updated_at: new Date().toISOString() }, { onConflict: "id", ignoreDuplicates: true })
        .select("*")
        .maybeSingle()
        .abortSignal(AbortSignal.timeout(5000));
      if (created) {
        data = created;
        // Send welcome email (fire-and-forget — new user only)
        const sess = await Promise.race([
          supabase.auth.getSession(),
          new Promise(resolve => setTimeout(() => resolve({ data: { session: null } }), 3000)),
        ]);
        const at = sess?.data?.session?.access_token;
        if (at) {
          fetch("/api/email/welcome", {
            method: "POST",
            headers: { Authorization: `Bearer ${at}`, "Content-Type": "application/json" },
            body: JSON.stringify({}),
          }).catch(() => {});
        }
      } else {
        // ignoreDuplicates returned no row — the profile already exists and the
        // initial fetch just hiccuped. Refetch the real row so we don't leave
        // the user in a "no profile" state.
        _D && console.log("[auth:debug] upsert was a no-op (row exists) — refetching");
        const { data: existing } = await supabase
          .from("profiles").select("*").eq("id", userId).single()
          .abortSignal(AbortSignal.timeout(5000));
        if (existing) data = existing;
      }
    }

    // Session 46 C6: silent automatic retrial. If the user lapsed
    // ≥90 days ago AND has never been retrialed, extend their trial
    // by 14 days. No UI, no email, no fanfare — matches the
    // "status inferred, not announced" rule. Fülkit's welcome-back
    // message (already shipped) fires on next chat visit since this
    // flips them from lapsed to not-lapsed.
    if (data && isRetrialEligible(data)) {
      try {
        const newEnd = new Date(Date.now() + TRIAL_DURATION_DAYS * 86400000).toISOString();
        const nowIso = new Date().toISOString();
        const { data: updated } = await supabase
          .from("profiles")
          .update({ trial_ends_at: newEnd, retrialed_at: nowIso })
          .eq("id", userId)
          .select("*")
          .single();
        if (updated) data = updated;
      } catch (e) {
        _D && console.log("[auth:debug] retrial write failed:", e?.message);
      }
    }

    if (data) {
      _D && console.log("[auth:debug] setProfile with role=" + data.role + " seat=" + data.seat_type, Math.round(performance.now() - _t0) + "ms");
      setProfile(data);
      setHasContext(data.onboarded === true || (count || 0) > 0);

      // Build onboarding state (skip for owner in dev mode)
      const tiers = tierRows || [];
      const progress = progressRows || [];
      if (data.role !== "owner" && tiers.length > 0) {
        const tiersCompleted = progress.filter((p) => p.completed_at).length;
        const currentTier = tiersCompleted + 1;
        const totalTiers = tiers.length;
        const isOnboardingDone = tiersCompleted >= totalTiers;

        const trialStarted = data.trial_started_at ? new Date(data.trial_started_at) : null;
        const trialDay = trialStarted
          ? Math.floor((Date.now() - trialStarted.getTime()) / 86400000) + 1
          : 1;

        // Find pending trigger: current tier where questions answered but assignment not done
        let pendingTrigger = null;
        if (!isOnboardingDone) {
          const currentProgress = progress.find((p) => p.tier_num === currentTier);
          if (currentProgress && !currentProgress.assignment_done) {
            const tierDef = tiers.find((t) => t.tier_num === currentTier);
            pendingTrigger = tierDef?.completion_trigger || null;
          }
        }

        setOnboardingState({
          currentTier: Math.min(currentTier, totalTiers + 1),
          tiersCompleted,
          totalTiers,
          trialDay,
          trialDaysRemaining: Math.max(0, PLANS.trial.durationDays - trialDay),
          isInTrial: trialDay <= PLANS.trial.durationDays,
          isOnboardingDone,
          tierProgress: progress,
          pendingTrigger,
        });
      }

      // Check for recently connected integrations (within last 24hr)
      try {
        const { data: recentInteg } = await supabase
          .from("integrations")
          .select("provider, created_at")
          .eq("user_id", userId)
          .gte("created_at", new Date(Date.now() - 86400000).toISOString())
          .order("created_at", { ascending: false })
          .limit(1);
        if (recentInteg?.length > 0) {
          const dismissed = localStorage.getItem(`fulkit-integ-tip-${recentInteg[0].provider}`);
          if (!dismissed) {
            setNewlyConnectedIntegration(recentInteg[0]);
          }
        }
      } catch {
        // integrations table may not have expected columns — skip silently
      }
    }
    _D && console.log("[auth:debug] fetchProfile COMPLETE", Math.round(performance.now() - _t0) + "ms", data ? "SUCCESS" : "NO PROFILE");
    return data;
    })(); // end fetchPromise IIFE

    fetchingRef.current = fetchPromise;
    try {
      return await fetchPromise;
    } finally {
      fetchingRef.current = null; // allow future refreshes (e.g. after message sent)
    }
  }, []);

  useEffect(() => {
    // Skip auth init on the callback page — it handles its own exchange
    if (window.location.pathname === "/auth/callback") {
      setLoading(false);
      return;
    }

    // Real auth — 7s hard cap guarantees loading always resolves
    const _authStart = performance.now();
    _D && console.log("[auth:debug] === AUTH INIT START ===");
    const initAuth = async () => {
      try {
        _D && console.log("[auth:debug] getSession START");
        const { data: { session } } = await supabase.auth.getSession();
        _D && console.log("[auth:debug] getSession DONE", Math.round(performance.now() - _authStart) + "ms", session ? "HAS SESSION" : "NO SESSION");
        if (session?.user) {
          const u = session.user;
          setUser({
            id: u.id,
            email: u.email,
            name: u.user_metadata?.full_name || "",
          });
          setAccessToken(session.access_token);
          _D && console.log("[auth:debug] user set, calling fetchProfile", Math.round(performance.now() - _authStart) + "ms");
          await fetchProfile(u.id);
          _authResolved = true;
          _D && console.log("[auth:debug] fetchProfile returned, _authResolved=true", Math.round(performance.now() - _authStart) + "ms");
          // Seed profiles.name from Google if not already set
          const googleName = u.user_metadata?.full_name;
          if (googleName) {
            supabase.from("profiles").update({ name: googleName }).eq("id", u.id).is("name", null).then(() => {});
          }
        }
      } catch (err) {
        console.error("[auth] init failed:", err.message, Math.round(performance.now() - _authStart) + "ms");
      }
    };
    let _hardCapTimer = null;
    Promise.race([
      initAuth().then(r => { clearTimeout(_hardCapTimer); return r; }),
      new Promise(resolve => { _hardCapTimer = setTimeout(() => { _D && console.log("[auth:debug] 7s HARD CAP fired — setLoading(false) forced", Math.round(performance.now() - _authStart) + "ms"); resolve(); }, 7000); }),
    ]).then(() => { _D && console.log("[auth:debug] setLoading(false)", Math.round(performance.now() - _authStart) + "ms"); setLoading(false); });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        _D && console.log("[auth:debug] onAuthStateChange event=" + _event, session ? "HAS SESSION" : "NO SESSION");
        if (session?.user) {
          const u = session.user;
          setUser({
            id: u.id,
            email: u.email,
            name: u.user_metadata?.full_name || "",
          });
          setAccessToken(session.access_token);
          // Only fetch profile on initial load or new sign-in — not on token refresh
          if (_event === "INITIAL_SESSION") {
            _D && console.log("[auth:debug] onAuthStateChange calling fetchProfile for", _event);
            await fetchProfile(u.id);
            _D && console.log("[auth:debug] onAuthStateChange fetchProfile returned");
          } else {
            _D && console.log("[auth:debug] onAuthStateChange skipping fetchProfile for", _event, "(token-only update)");
          }
        } else {
          setUser(null);
          setProfile(null);
          setAccessToken(null);
        }
        setLoading(false);

        // Auth event observability (Pending #28). Session-level events were
        // previously silent — only sign-in callback errors fired signals.
        // These cover the gaps: sign-out, refresh failure, and unexpected
        // session loss.
        try {
          if (_event === "SIGNED_OUT") {
            emitSignal("auth_signed_out", "info", {});
          } else if (_event === "TOKEN_REFRESHED" && !session) {
            emitSignal("auth_token_refresh_failed", "warning", {});
          } else if (_event === "USER_UPDATED" && !session) {
            emitSignal("auth_session_lost", "error", {});
          }
        } catch {}
      }
    );

    // Refresh auth when tab wakes from sleep or network reconnects
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        supabase.auth.refreshSession().catch(() => {});
      }
    };
    const onOnline = () => {
      supabase.auth.refreshSession().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    // Proactive token refresh every 10 minutes — prevents stale tokens
    // during long sessions where the user stays on the same tab.
    // Supabase JWTs expire after ~1 hour; this keeps them fresh.
    const tokenRefresh = setInterval(() => {
      supabase.auth.getSession().catch(() => {});
    }, 600000);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      clearInterval(tokenRefresh);
    };
  }, [fetchProfile]);

  const signIn = useCallback(async (provider = "google") => {
    const isApp = typeof window !== "undefined" && localStorage.getItem("fulkit_app") === "1";
    const cap = window.Capacitor;

    // Native app: PKCE + Browser plugin + custom URL scheme
    // 1. Generate OAuth URL (stores PKCE verifier in localStorage)
    // 2. Open system browser (Google allows it, unlike WebView)
    // 3. After sign-in, Supabase redirects to fulkit://auth/callback?code=XXX
    // 4. CapacitorInit's appUrlOpen listener routes to /auth/callback page
    // 5. Callback page exchanges code using stored PKCE verifier
    if (isApp && cap?.isNativePlatform?.() && cap?.Plugins?.Browser) {
      console.log("[auth] native flow: PKCE + Browser plugin");
      try {
        const { data, error: oauthErr } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: "fulkit://auth/callback",
            skipBrowserRedirect: true,
            queryParams: { prompt: "select_account" },
          },
        });
        if (oauthErr || !data?.url) {
          console.error("[auth] failed to generate OAuth URL:", oauthErr);
          return;
        }
        console.log("[auth] opening system browser for OAuth");
        await cap.Plugins.Browser.open({ url: data.url });
      } catch (err) {
        console.error("[auth] native sign-in error:", err);
      }
      return;
    }

    // Web: normal PKCE redirect flow
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: { prompt: "select_account" },
      },
    });
    if (error) {
      console.error("Sign in error:", error.message);
      // Pending #28: previously this was a silent console.error
      try { emitSignal("auth_signin_failed", "error", { provider, message: error.message }); } catch {}
    }
  }, []);

  const signInWithEmail = useCallback(async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) {
      console.error("Magic link error:", error.message);
      if (error.message.toLowerCase().includes("rate limit")) {
        try { emitSignal("auth_rate_limit", "warning", { method: "magic_link", message: error.message }); } catch {}
      }
    }
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn("[auth] signOut error:", err.message);
    }
    setUser(null);
    setProfile(null);
    window.location.href = "/";
  }, []);

  // Check GitHub connection when we have a token
  const checkGitHub = useCallback(async (token) => {
    try {
      const res = await fetch("/api/github/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGithubConnected(data.connected);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (accessToken) checkGitHub(accessToken);
  }, [accessToken, checkGitHub]);

  // Debug mode — sync the runtime flag whenever profile changes. Keeps
  // debug.log() gated correctly across login, logout, and profile
  // refreshes. Stale-flag rule (60-min auto-disable) handled inside
  // getEffectiveDebugMode — clears stale "on" states automatically on
  // every profile read without requiring a cron.
  useEffect(() => {
    setDebugEnabled(getEffectiveDebugMode(profile));
  }, [profile]);

  const isOwner = profile?.role === "owner";
  const isOnboarded = profile?.onboarded ?? false;

  // ─── authFetch: always-fresh-token fetch wrapper ────────
  // Use instead of raw fetch + accessToken for any authenticated API call.
  // Gets a fresh session token every call — no stale JWT issues.
  // On 401, refreshes the session ONCE and retries — turns expired-token
  // mass-401s into transparent recoveries instead of stranding the user.
  const authFetch = useCallback(async (url, opts = {}) => {
    let token = accessToken;
    if (!token) {
      // No cached token — must fetch fresh session
      const _af0 = performance.now();
      try {
        _D && console.log("[chat:debug] authFetch getSession START for", url, "(no cached token)");
        const { data: { session } } = await Promise.race([
          supabase.auth.getSession(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("getSession timeout")), 5000)),
        ]);
        if (session?.access_token) token = session.access_token;
        _D && console.log("[chat:debug] authFetch getSession DONE", Math.round(performance.now() - _af0) + "ms");
      } catch (e) {
        console.warn("[chat:debug] authFetch getSession FAILED", Math.round(performance.now() - _af0) + "ms", e.message);
      }
    }
    if (!token) console.warn("[chat:debug] authFetch NO TOKEN for", url);

    const buildHeaders = (t) => ({
      ...opts.headers,
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    });

    // Network-error retry — single silent retry on transient blips. TypeError
    // from fetch ("Failed to fetch") is the network_error class that fires
    // false-positive `fetch_error` signals when a Wi-Fi flicker or short
    // outage hits mid-request. POST/PUT bodies are streams that may have
    // been consumed by the first attempt → skip retry if a body is present
    // (real failures still surface).
    let res;
    try {
      res = await fetch(url, { ...opts, headers: buildHeaders(token) });
    } catch (err) {
      if (err?.name === "TypeError" && !opts.body) {
        res = await fetch(url, { ...opts, headers: buildHeaders(token) });
      } else {
        throw err;
      }
    }

    // 401 retry — Supabase access tokens expire ~1hr. Refresh once and
    // retry transparently. If refresh also fails (refresh token gone),
    // the original 401 propagates and the caller can show a re-login UX.
    if (res.status === 401) {
      console.warn("[authFetch] 401 on", url, "— refreshing session and retrying once");
      try {
        const { data: { session }, error } = await supabase.auth.refreshSession();
        if (!error && session?.access_token && session.access_token !== token) {
          // Body streams can only be consumed once; clone opts.body if needed.
          // For our codebase body is always a string (JSON.stringify) so re-using opts is safe.
          res = await fetch(url, { ...opts, headers: buildHeaders(session.access_token) });
          if (res.ok) console.log("[authFetch] retry succeeded after refresh");
        }
      } catch (refreshErr) {
        console.warn("[authFetch] refresh failed:", refreshErr?.message);
      }
    }

    return res;
  }, [accessToken]);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        accessToken,
        authFetch,
        signIn,
        signInWithEmail,
        signOut,
        isOwner,
        isOnboarded,
        isLapsed: isLapsedTrial(profile),
        hasContext,
        fetchProfile,
        githubConnected,
        setGithubConnected,
        checkGitHub,
        compactMode,
        setCompactMode,
        onboardingState,
        newlyConnectedIntegration,
        dismissIntegrationTip: (provider) => {
          localStorage.setItem(`fulkit-integ-tip-${provider}`, "1");
          setNewlyConnectedIntegration(null);
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
